import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, Inject, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoaderService } from '../../../core/services/loader.service';
import { GoogleAuthService } from '../../../core/services/google-auth.service';
import { FlashMessageService } from '../flash-message/flash-message.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { GoogleUserProfile, SocialUserModel } from '../../../models/user.model';
import { first, interval, take } from 'rxjs';
import { BootstrapModalService } from '../../../core/services/bootstrap-modal.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LinkedInAuthService } from '../../../core/services/linkedin-auth.service';
import { AuthService } from '../../../features/auth/auth.service';
import { CountryByIpService } from '../../../core/services/country-by-ip.service';

@Component({
  selector: 'app-login-popup',
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, RouterModule],
  templateUrl: './login-popup.component.html',
  styleUrl: './login-popup.component.scss'
})
export class LoginPopupComponent implements OnInit, AfterViewInit {

  @ViewChild('loginBtn', { static: true }) loginBtn!: ElementRef;
  @ViewChild('loginBtnClose', { static: true }) loginBtnClose!: ElementRef;
  @ViewChild('resetBtn', { static: true }) resetBtn!: ElementRef;
  @ViewChild('resetCloseBtn', { static: true }) resetCloseBtn!: ElementRef;
  @ViewChild('emailSentBtn', { static: true }) emailSentBtn!: ElementRef;

  private readonly destroyRef = inject(DestroyRef);

  loginForm!: FormGroup;
  forgotPasswordForm!: FormGroup;
  language!: string;
  isBrowser: boolean;
  isSocialUser = false;
  returnUrl!: string;
  isSubmittingReset = false;
  loading = false;
  loading2 = false;
  submitted = false;
  error!: string;
  inValidUser = false;
  screenHeight = '';
  currentYear: any;
  showPassword = false;
  isSubmitting = false;
  errorMessage: string | null = null;
  message: string = '';
  email: string = '';
  isMailSent: boolean = false;
  socialUser: SocialUserModel = new SocialUserModel();
  socialLinkedAccount: boolean = false;
  socialProvider: string = '';
  showPasswordStep = false;
  checkingEmail = false;
  otpForm!: FormGroup;
  verifyingOtp = false;
  resendCooldown = 30;
  resendTimer: any;
  canResendOtp = false;
  otpSubmitted = false;
  invalidOtp = false;
  selectedLang: string = '';

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly authenticationService: AuthenticationService,
    public translate: TranslateService,
    public loaderService: LoaderService,
    private readonly googleAuth: GoogleAuthService,
    private readonly flashMessage: FlashMessageService,
    private readonly bootstrapModalService: BootstrapModalService,
    private readonly linkedInAuth: LinkedInAuthService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: AuthService,
    private readonly countryByIpService: CountryByIpService

  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.googleAuth.init((response: any) => this.handleCredentialResponse(response));
    }, 500);
  }

  // ngAfterViewInit(): void {
  //   this.initializeGoogle();
  // }

  // private async initializeGoogle(): Promise<void> {
  //   this.googleAuth.init((response) => this.handleCredentialResponse(response));
  // }

  ngOnInit() {
    this.loginForm = this.formBuilder.group({
      username: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
      password: ['']
    });

    this.forgotPasswordForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });
    // this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
    this.otpForm = this.formBuilder.group({ otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]] });

    if (this.authenticationService.currentUserValue) {
      this.loginBtnClose.nativeElement.click();
    }
  }

  get username(): FormControl {
    return this.loginForm.get('username') as FormControl;
  }

  get password(): FormControl {
    return this.loginForm.get('password') as FormControl;
  }

  get forgotPasswordEmail(): FormControl {
    return this.forgotPasswordForm.get('email') as FormControl;
  }

  async handleCredentialResponse(response: any): Promise<void> {

    const accessToken = response?.access_token;
    if (!accessToken) return;
    this.loaderService.display(true);
    try {
      const res = await fetch(
        'https://www.googleapis.com/oauth2/v3/userinfo',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );

      if (!res.ok) {
        throw new Error(`Google API error: ${res.status}`);
      }

      const profile: GoogleUserProfile = await res.json();

      if (!profile?.sub) {
        throw new Error('Invalid Google profile response');
      }
      this.isSocialUser = true;
      this.socialUser.GoogleId = profile.sub;
      this.socialUser.Provider = 'Google';
      this.setSocialData(profile);

    } catch (error) {
      console.error('Google login failed:', error);
      this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
    } finally {
      this.loaderService.display(false);
    }
  }

  checkIsIframeUser() {
    return this.router.url.includes('/manufacturer/');
  }

  async onSubmit() {
    this.submitted = true;
    this.isSocialUser = false;
    this.inValidUser = false;
    this.socialLinkedAccount = false;
    this.errorMessage = null;
    if (this.isBrowser) {
      localStorage.setItem('isSocialUser', JSON.stringify(false));
      localStorage.removeItem('socialuser');
    }
    if (this.username.invalid) {
      this.username.markAsTouched();
      return;
    }
    if (!this.showPasswordStep) {
      this.onContinue();
      return;
    }

    if (this.password.invalid) {
      this.password.markAsTouched();
      return;
    }

    this.loading = true;
    this.isSubmitting = true;
    if (this.isBrowser) {
      const countryId = await this.countryByIpService.getCountry();
      this.selectedLang = countryId === 'IT' ? 'it' : 'en';
    }

    this.loaderService.display(true);
    this.authenticationService.login(this.username?.value, this.password?.value).pipe(first()).subscribe({
      next: (data: any) => {
        if (!this.isBrowser) return;
        const returnUrl = localStorage.getItem('returnUrl');

        this.loginForm.reset();
        this.showPasswordStep = false;
        this.loginBtnClose.nativeElement.click();

        if (this.checkIsIframeUser() && returnUrl && data?.RoleId === 1) {
          this.authenticationService.logout();
          this.bootstrapModalService.open('companyAccountRestrictedModal');
          this.loaderService.display(false);
          return;
        }
        if (this.checkIsIframeUser() && returnUrl) {
          // localStorage.removeItem('returnUrl');
          this.router.navigateByUrl(returnUrl);
        }
        this.loaderService.display(false);
      },
      error: (err) => {
        if (err?.error?.isSocialUser) {
          this.socialLinkedAccount = true;
          this.socialProvider = err.error.provider;
        }
        this.inValidUser = true;
        this.loading = false;
        this.isSubmitting = false;
        this.errorMessage = this.translate.instant('login-11');
        this.loaderService.display(false);
      },
      complete: () => {
        this.loading = false;
        this.isSubmitting = false;
        this.loaderService.display(false);
      }
    });
  }

  togglePassword() { this.showPassword = !this.showPassword; }

  openResetPopup() {
    this.goBack();
    this.resetBtn.nativeElement.click();
  }

  triggerGoogleSignIn() {
    this.googleAuth.signIn();
  }

  onLinkedIn() {
    if (!this.isBrowser) return;

    this.linkedInAuth.authenticate().then((data: any) => {
      this.isSocialUser = true;
      this.socialUser.LinkedInId = data.socialId;
      this.socialUser.Provider = 'LinkedIn'
      this.setSocialData(data);
    })
      .catch((err: any) => {
        console.error('LinkedIn error:', err);
        this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
      });
  }


  setSocialData(data: any) {
    this.loaderService.display(true);
    this.inValidUser = false;
    this.socialLinkedAccount = false;
    this.submitted = true;
    this.loading = true;
    this.isSubmitting = true;
    this.socialUser.FirstName = data.name;
    this.socialUser.Email = data.email;

    this.authenticationService.checkIsRegistered(this.socialUser).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.loaderService.display(false);
        if (this.isBrowser) {
          this.loginForm.reset();
          this.cdr.detectChanges();
          this.bootstrapModalService.close('loginPopupModal');
          const returnUrl = localStorage.getItem('returnUrl');

          if (this.checkIsIframeUser() && returnUrl && response?.RoleId === 1) {
            this.bootstrapModalService.open('companyAccountRestrictedModal');
            this.inValidUser = false;
            this.loading = false;
            this.isSubmitting = false;
            this.submitted = false;
            return;
          }
        }
        this.setSocialUser(response);
      },
      error: (error) => {
        if (error.error.message === 'USER NOT FOUND' && this.isBrowser) {
          this.loginForm.reset();
          this.inValidUser = true;
          this.loading = false;
          this.isSubmitting = false;
          this.errorMessage = this.translate.instant('login-11');
          this.setSocialUserForRegister();
          this.socialUser = new SocialUserModel();
          this.bootstrapModalService.close('loginPopupModal');
          if (this.checkIsIframeUser()) {
            this.router.navigate(['manufacturer/auth/register'], { queryParams: { email: data.email } });
          } else {
            this.router.navigate(['/auth/register'], { queryParams: { email: data.email } });
          }

        } else {
          this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
        }
        this.loaderService.display(false);
      }
    });
  }

  setSocialUserForRegister() {
    if (this.isBrowser) {
      localStorage.setItem('isSocialUser', JSON.stringify(true));
      localStorage.setItem('socialuser', JSON.stringify(this.socialUser));
      this.inValidUser = false;
      this.loading = false;
      this.isSubmitting = false;
      this.submitted = false;
    }
    this.bootstrapModalService.close('loginPopupModal');
    this.loaderService.display(false);
  }

  setSocialUser(result: any) {
    const user = result;
    if (this.isBrowser) {
      localStorage.setItem('currentUser', JSON.stringify(user || null));
      localStorage.setItem('isLogin', JSON.stringify(true));
      localStorage.setItem('token', user.Token);
      localStorage.setItem('isSocialUser', JSON.stringify(true));
      localStorage.setItem('socialuser', JSON.stringify(this.socialUser));
      this.authenticationService.isUserLoggedIn = true;
      this.authenticationService.currentUserSubject.next(user);
      this.authenticationService.isUserLoggedIn$.next(true);
      this.loaderService.currentUser = user;
      this.inValidUser = false;
      this.loading = false;
      this.isSubmitting = false;
      this.submitted = false;
    }
    this.bootstrapModalService.close('loginPopupModal');
    this.loaderService.display(false);
  }

  onForgotPassword() {
    if (this.forgotPasswordForm.invalid) return;
    this.loading2 = true;
    this.email = this.forgotPasswordForm.value.email;
    this.loaderService.display(true);
    this.authenticationService.checkEmail(this.forgotPasswordForm.value.email, this.translate.getCurrentLang()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        let result = data.message;
        this.loading2 = false;
        if (result == 'Mail sent.') {
          this.isMailSent = true;
          this.message = 'mail-sent';
        }
        else if (result == 'Mail not sent.') {
          this.message = 'mail-not-sent';
        }
        else if (result == 'No Account.') {
          this.message = 'no-account';
        }
        this.forgotPasswordForm.reset();
        this.resetCloseBtn.nativeElement.click();
        this.loaderService.display(false);
        this.emailSentBtn.nativeElement.click();

      },
      error: err => {
        this.loading2 = false;
        this.errorMessage = err;
        this.loaderService.display(false);
        this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
      }
    });
  }

  onContinue() {
    this.inValidUser = false;
    this.errorMessage = null;

    if (this.username.invalid) {
      this.username.markAsTouched();
      return;
    }

    this.checkingEmail = true;
    this.loaderService.display(true);

    this.authenticationService.checkIsUserExist(this.username.value).subscribe({
      next: (exists: boolean) => {
        this.loaderService.display(false);
        this.checkingEmail = false;

        if (exists) {
          this.showPasswordStep = true;
          this.password.setValidators([Validators.required]);
          this.password.updateValueAndValidity();
          return;
        }
        this.bootstrapModalService.close('loginPopupModal');
        if (this.checkIsIframeUser()) {
          this.router.navigate(['manufacturer/auth/register'], { queryParams: { email: this.username.value } });
        } else {
          this.router.navigate(['/auth/register'], { queryParams: { email: this.username.value } });
        }
      },

      error: () => {
        this.loaderService.display(false);
        this.checkingEmail = false;
        this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
      }
    });
  }

  async onSendLoginOtp() {
    if (this.isBrowser) {
      const countryId = await this.countryByIpService.getCountry();
      this.selectedLang = countryId === 'IT' ? 'it' : 'en';
    }
    this.loaderService.display(true);
    this.authService.sendOtpForLogin(this.username.value, this.selectedLang).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.loaderService.display(false);
        this.otpForm.reset();
        this.startResendCooldown();
        this.goBack();
        this.loginBtnClose.nativeElement.click();
        this.bootstrapModalService.open('loginOtpModal');
        this.flashMessage.showSuccessAlert(this.translate.instant('verification_code_sent'));
      },
      error: () => {
        this.loaderService.display(false);
        this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
      }
    });
  }

  startResendCooldown(): void {
    if (!this.isBrowser) return;
    this.canResendOtp = false;
    this.resendCooldown = 30;

    interval(1000).pipe(take(30)).subscribe({
      next: () => this.resendCooldown--,
      complete: () => this.canResendOtp = true
    });
  }

  verifyOtp() {
    this.otpSubmitted = true;
    if (this.otpForm.invalid) {
      this.otpForm.markAllAsTouched();
      return;
    }

    this.verifyingOtp = true;
    this.loaderService.display(true);
    this.authService.verifyOtpForLogin(this.username.value, this.otpForm.value.otp).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        if (data?.Token) {
          this.verifyingOtp = false;
          if (this.isBrowser) {
            const returnUrl = localStorage.getItem('returnUrl');
            // Prevent company accounts from logging into the iFrame
            if (this.checkIsIframeUser() && returnUrl && data?.Token.RoleId === 1) {
              this.bootstrapModalService.close('loginOtpModal');
              this.loginForm.reset();
              this.showPasswordStep = false;
              this.bootstrapModalService.open('companyAccountRestrictedModal')
              this.loaderService.display(false);
              return;
            }

            localStorage.setItem('currentUser', JSON.stringify(data?.Token));
            localStorage.setItem('isLogin', JSON.stringify(true));
            localStorage.setItem('token', data?.Token.Token);
            this.authenticationService.currentUserSubject.next(data?.Token);
            this.authenticationService.isUserLoggedIn = true;
            this.authenticationService.isUserLoggedIn$.next(true);

            if (returnUrl && this.checkIsIframeUser()) {
              // localStorage.removeItem('returnUrl');
              this.router.navigateByUrl(returnUrl);
            }
          }
          this.bootstrapModalService.close('loginOtpModal')

          this.loginForm.reset();
          this.showPasswordStep = false;
          this.loaderService.display(false);
        } else {
          this.invalidOtp = true;
          this.verifyingOtp = false;
          this.loaderService.display(false);
        }
      },
      error: () => {
        this.verifyingOtp = false;
        this.loaderService.display(false);
        this.flashMessage.showErrorAlert(this.translate.instant('invalid_otp'));
      }
    });
  }

  async resendOtp() {
    if (!this.canResendOtp) return;
    this.invalidOtp = false;
    this.canResendOtp = false;
    this.startResendCooldown();
    if (this.isBrowser) {
      const countryId = await this.countryByIpService.getCountry();
      this.selectedLang = countryId === 'IT' ? 'it' : 'en';
    }
    this.loaderService.display(true);
    this.authService.sendOtpForLogin(this.username.value, this.selectedLang).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.loaderService.display(false);
        this.flashMessage.showSuccessAlert(this.translate.instant('otp_resent'));
      },
      error: () => {
        this.loaderService.display(false);
        this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
      }
    });
  }

  goBack() {
    this.showPasswordStep = false;
    this.password.reset();
    this.password.clearValidators();
    this.password.updateValueAndValidity();
    this.inValidUser = false;
    this.errorMessage = null;
  }
}
